/**
 * Zero-dependency imperative navigation registry.
 *
 * Callers that live outside the React tree (e.g. toast content rendered by
 * `sonner`, which mounts as a sibling of the router provider) cannot use
 * `useNavigate()`. They also must not import `@/routes` directly: that pulls
 * the entire route tree (Root, dashboardRoutes, teamsRoutes, every page) into
 * their module graph, which is what made vitest's global `afterEach` hooks
 * exceed their 10s timeout when the tracker's toast modules loaded lazily.
 *
 * Instead, `routes/index.tsx#createRouter()` registers the live data router's
 * `navigate` here at bootstrap, and out-of-tree callers import only this tiny
 * module. Falls back to a full page load if nothing is registered (e.g. a
 * toast fired before the router existed — not expected in practice, since
 * `createRouter()` runs during app bootstrap in `main.tsx`).
 */

type AppNavigateFn = (path: string) => void;

let registeredNavigate: AppNavigateFn | null = null;

/** Register (or clear, with `null`) the SPA's imperative navigate function. */
export function registerAppNavigate(fn: AppNavigateFn | null): void {
  registeredNavigate = fn;
}

/**
 * Navigate to a conversation without a full page reload. In-app SPA
 * navigation via the registered router; hard navigation only as a fallback
 * when no router has been registered.
 */
export function navigateToConversation(
  orgId: string,
  conversationId: string
): void {
  const path = `/${orgId}/conversations/${conversationId}`;
  if (registeredNavigate) {
    registeredNavigate(path);
  } else {
    window.location.assign(path);
  }
}
